import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { createTenantStorage } from './tenantStorage';

export type ComparerItemType = 'request' | 'response' | 'text';

export type ComparerItemMetadata = {
  method?: string;
  url?: string;
  status?: number;
  timestamp?: Date;
};

export type ComparerItem = {
  id: string;
  label: string;
  content: string;
  type: ComparerItemType;
  metadata?: ComparerItemMetadata;
};

export type ViewMode = 'side-by-side' | 'unified';

export type ComparerSettings = {
  viewMode: ViewMode;
  showLineNumbers: boolean;
  collapseUnchanged: boolean;
};

type ComparerStore = {
  items: ComparerItem[];
  selectedLeftId: string | null;
  selectedRightId: string | null;
  settings: ComparerSettings;
  totalDiffs: number;

  // Item actions
  addItem: (item: ComparerItem) => void;
  removeItem: (id: string) => void;
  clearItems: () => void;
  updateItemLabel: (id: string, label: string) => void;
  reorderItems: (fromIndex: number, toIndex: number) => void;

  // Selection actions
  setSelectedLeft: (id: string | null) => void;
  setSelectedRight: (id: string | null) => void;
  swapSelection: () => void;
  autoSelectItems: () => void;

  // Settings actions
  updateSettings: (settings: Partial<ComparerSettings>) => void;

  // Navigation actions
  setTotalDiffs: (count: number) => void;
};

const defaultSettings: ComparerSettings = {
  viewMode: 'side-by-side',
  showLineNumbers: true,
  collapseUnchanged: false,
};

export const useComparerStore = create<ComparerStore>()(
  persist(
    (set) => ({
      items: [],
      selectedLeftId: null,
      selectedRightId: null,
      settings: defaultSettings,
      totalDiffs: 0,

      addItem: (item) =>
        set((state) => {
          const newItems = [...state.items, item];
          // Auto-select if we now have exactly 2 items
          if (newItems.length === 1) {
            return { items: newItems, selectedLeftId: item.id };
          }
          if (newItems.length === 2 && !state.selectedRightId) {
            return { items: newItems, selectedRightId: item.id };
          }
          return { items: newItems };
        }),

      removeItem: (id) =>
        set((state) => {
          const newItems = state.items.filter((item) => item.id !== id);
          const updates: Partial<ComparerStore> = { items: newItems };

          // Clear selection if removed item was selected
          if (state.selectedLeftId === id) {
            updates.selectedLeftId = null;
          }
          if (state.selectedRightId === id) {
            updates.selectedRightId = null;
          }

          return updates;
        }),

      clearItems: () =>
        set({
          items: [],
          selectedLeftId: null,
          selectedRightId: null,
          totalDiffs: 0,
        }),

      updateItemLabel: (id, label) =>
        set((state) => ({
          items: state.items.map((item) =>
            item.id === id ? { ...item, label } : item,
          ),
        })),

      reorderItems: (fromIndex, toIndex) =>
        set((state) => {
          const newItems = [...state.items];
          const [movedItem] = newItems.splice(fromIndex, 1);
          newItems.splice(toIndex, 0, movedItem);
          return { items: newItems };
        }),

      setSelectedLeft: (id) => set({ selectedLeftId: id }),

      setSelectedRight: (id) => set({ selectedRightId: id }),

      swapSelection: () =>
        set((state) => ({
          selectedLeftId: state.selectedRightId,
          selectedRightId: state.selectedLeftId,
        })),

      autoSelectItems: () =>
        set((state) => {
          if (state.items.length === 0) {
            return { selectedLeftId: null, selectedRightId: null };
          }
          if (state.items.length === 1) {
            return { selectedLeftId: state.items[0].id, selectedRightId: null };
          }
          // Select first two items
          return {
            selectedLeftId: state.items[0].id,
            selectedRightId: state.items[1].id,
          };
        }),

      updateSettings: (newSettings) =>
        set((state) => ({
          settings: { ...state.settings, ...newSettings },
        })),

      setTotalDiffs: (count) => set({ totalDiffs: count }),
    }),
    {
      name: 'terra-comparer-store',
      storage: createJSONStorage(() => createTenantStorage()),
      partialize: (state) => ({
        items: state.items,
        selectedLeftId: state.selectedLeftId,
        selectedRightId: state.selectedRightId,
        settings: state.settings,
      }),
    },
  ),
);
